import { useEffect, useMemo, useState } from "react";
import { referenceDataService, type Country } from "@/services/referenceDataService";

let cachedCountries: Country[] | null = null;
let fetchPromise: Promise<Country[]> | null = null;

function fetchCountries(): Promise<Country[]> {
  if (cachedCountries) return Promise.resolve(cachedCountries);
  if (!fetchPromise) {
    fetchPromise = referenceDataService
      .getCountries()
      .then((data) => {
        cachedCountries = data;
        return data;
      })
      .catch((err) => {
        fetchPromise = null;
        throw err;
      });
  }
  return fetchPromise;
}

export function useCountries() {
  const [countries, setCountries] = useState<Country[]>(cachedCountries ?? []);
  const [loading, setLoading] = useState(!cachedCountries);

  useEffect(() => {
    if (cachedCountries) {
      setCountries(cachedCountries);
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchCountries()
      .then((data) => {
        if (!cancelled) {
          setCountries(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const countryCodeOptions = useMemo(
    () => countries.map((c) => ({ value: c.value, label: `${c.label} (${c.value})` })),
    [countries],
  );

  const phoneCodeOptions = useMemo(
    () => countries.map((c) => ({ value: `${c.code}|${c.value}`, label: c.code, subtitle: c.label })),
    [countries],
  );

  return { countries, countryCodeOptions, phoneCodeOptions, loading };
}
